import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Truck, ShieldCheck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSettings = () => {
  const [settings, setSettings] = useState({ cod_enabled: true, online_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/payment`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    setSaving(true);
    try {
      await axios.post(`${API}/settings/payment`, newSettings);
      toast.success('Payment settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
      // Revert on error
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-bold uppercase">Payment <span className="text-primary">Settings</span></h2>
      </div>

      <Card className="p-6 border-slate-200">
        <p className="text-slate-600 mb-8">
          Configure which payment methods are available to customers during checkout.
        </p>

        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-full">
                <Truck className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Cash on Delivery (COD)</h3>
                <p className="text-sm text-slate-500">Allow customers to pay when they receive their order</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold uppercase ${settings.cod_enabled ? 'text-green-600' : 'text-slate-400'}`}>
                {settings.cod_enabled ? 'Active' : 'Disabled'}
              </span>
              <Switch
                checked={settings.cod_enabled}
                onCheckedChange={(checked) => handleToggle('cod_enabled', checked)}
                disabled={saving}
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-full">
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Online Payment (Cashfree)</h3>
                <p className="text-sm text-slate-500">Enable Credit/Debit Card, UPI, and Net Banking via Cashfree</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold uppercase ${settings.online_enabled ? 'text-blue-600' : 'text-slate-400'}`}>
                {settings.online_enabled ? 'Active' : 'Disabled'}
              </span>
              <Switch
                checked={settings.online_enabled}
                onCheckedChange={(checked) => handleToggle('online_enabled', checked)}
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-primary mt-0.5" />
          <div className="text-sm text-slate-600">
            <p className="font-semibold text-slate-800 mb-1">Safety Note</p>
            <p>Disabling both options will prevent customers from completing any new orders. Always ensure at least one payment method is active during business hours.</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PaymentSettings;
